import './App.css';
import Nav from './NavBar/Nav';
import AdminPage from './pages/AdminPage';
import NotificationsPage from './pages/NotificationsPage';
import PermissionRequestsPage from './pages/PermissionRequestsPage';
import SearchUsersPage from './pages/SearchUsersPage';
import GroupsPage from './pages/GroupsPage';
import NewUserManagementPage from './pages/NewUserManagement';
import { Route, BrowserRouter, Navigate, Routes, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import NotificationsPane from './components/Notifications/NotificationsPane';
import {
  AcknowledgeNotification,
  GetNotifications,
  GetSupportRequests,
  Group,
  NotificationObject,
  SupportRequestObject,
} from './interfaces/APIInterface';
import PopUp from './components/PopUp';
import { COSMOS_API, POST_ENDPOINT, GET_ENDPOINT } from './api/API';
import { EditComponent } from './components/EditComponent';
import {
  AdminContextInterface,
  BasicIDToken,
  AdminApps,
  PermissionsList,
  RoleDefinition,
  ApplicationReference
} from './interfaces/ApplicationInterfaces';
//import { AdminContext } from './contexts/AdminContext';
import {
  MsalAuthenticationTemplate,
  useIsAuthenticated,
  useMsal,
} from '@azure/msal-react';
import {
  InteractionRequiredAuthError,
  InteractionType,
  InteractionStatus,
} from '@azure/msal-browser';
import { AccountInfo } from '@azure/msal-common';
import SupportRequestsPage from './pages/SupportRequestsPage';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
//import { createContext } from "react";
import { AdminContext, initAdminContext } from './contexts/AdminContext';

import { theme, backgroundUrl, backgroundColor } from './customization/styles';
import Footer from './customization/Footer';
import { SnackbarProvider } from 'notistack';
import { msalConfig } from './authConfig';
import { AppEnvSetting, isNullUndefEmptyObject } from './ts/common';
import AddUser from './components/AddUser';
import MassPreload from './components/MassPreload';
import useAccessToken from './hooks/accessToken';
import ResendInvite from './components/ResendInvite';
import SCIMApp from './components/SCIMApp';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

function App() {
  const { accounts, inProgress, instance } = useMsal();
  const [accountInfo, setAccountInfo] = useState<AccountInfo | undefined>();
  const { getToken, uiLocale, getTokenClaims } = useAccessToken();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const [notificationsModal, setNotificationsModal] = useState(false);
  const [notificationList, setNotificationList] = useState<
    Array<NotificationObject>
  >([]);
  const [notificationPane, setNotificationPane] = useState<
    Array<NotificationObject>
  >([]);
  const [supportRequestsList, setSupportRequestsList] = useState<
    Array<SupportRequestObject>
  >([]);
  const [msg, setMsg] = useState<string>('');
  const [showMsg, setShowMsg] = useState<boolean>(false);
  const [searchReload, setSearchReload] = useState(Date.now);
  const [requestsReload, setRequestsReload] = useState(Date.now);
  const [groupsReload, setGroupsReload] = useState(Date.now);
  const [allApplications, setAllApplications] =
    useState<Record<string, ApplicationReference>>();

  const simplifiedUI = AppEnvSetting.simplifiedUI;

  const msalClientId = AppEnvSetting.msalClientId;
  const msalScope = AppEnvSetting.msalScope;

      const findAccount = (): AccountInfo | undefined => {
        return accounts.find(
          (account) =>
            account.idTokenClaims !== undefined &&
            ( AppEnvSetting.authorityDomain.includes(
                  account.environment
                )) &&
            (account.idTokenClaims as BasicIDToken).aud === msalClientId &&
            (account.idTokenClaims as any).exp > Math.floor(Date.now() / 1000)
        )
      }

  const [adminCredentials, setAdminCredentials] =
    useState<AdminContextInterface>(initAdminContext());

  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None) {
      setIsLoading(true);
      var currentAdmin = accountInfo;
      if (currentAdmin === undefined) {
        currentAdmin = findAccount();
        if (currentAdmin === undefined) {
          const accessTokenRequest = {
            scopes: [msalScope],
            redirectUri: window.location.origin,
            account: undefined,
            extraQueryParameters: {
              ...((!!uiLocale) && { ui_locales: uiLocale })
            }
          };
          instance.acquireTokenRedirect(accessTokenRequest);
        }
        setAccountInfo({ ...currentAdmin! });
      }
      var allApps: Record<string, ApplicationReference> = {};
      COSMOS_API.POST({ includeAdmin: true }, POST_ENDPOINT.GetApplicationsList, getToken())
        .then((res) =>
          res.respBody.applications?.forEach((app: any) => {
            if (app.isActive === undefined || app.isActive)
              allApps = { ...allApps, [app.id]: {id: app.id, name: app.name, isGlobal: app.isGlobal } };
          })
        )
        .then(() => {
          setAllApplications(allApps);
          let claims: BasicIDToken = (currentAdmin?.idTokenClaims as BasicIDToken);
          COSMOS_API.GET(
            GET_ENDPOINT.GetEmployeeData,
            getToken()
          ).then((resp) => {
            if (
              resp.ok === false ||
              (!resp.respBody.employee.isSuperAdmin && !resp.respBody.employee.isOrganizationAdmin &&
                isNullUndefEmptyObject(resp.respBody.employee.applicationSpecificRoles))
            ) {
              return instance.logoutRedirect();
            }
            var adminApps: AdminApps;

            let emp = resp.respBody.employee;
            //Super admins get everything regardless of defined permissions
            if (emp.isSuperAdmin) {
              adminApps = Object.keys(allApps).map((key) => {
                return {
                  id: key,
                  name: allApps[key].name,
                  role: '',
                  isGlobal: allApps[key].isGlobal
                };
              });
            } else {
              adminApps = Object.keys(emp.applicationSpecificRoles).map(
                (key) => {
                  return {
                    id: key,
                    name: allApps[key].name,
                    role: emp.applicationSpecificRoles[key],
                    isGlobal: allApps[key].isGlobal
                  };
                }
              );
            }
            var groupListFetch = (AppEnvSetting.showGroupsPage || AppEnvSetting.showGroupsReviewRequests) ? 
            COSMOS_API.POST({}, POST_ENDPOINT.GetGroupsList, getToken())
            .then((res) => {
              return res.respBody.groups?.reduce((acc: Record<string, string>, group: Group) =>
                ({ ...acc, [group.id]: group }), {});
            }) : Promise.resolve({});
            COSMOS_API.POST(
              {},
              POST_ENDPOINT.EmployeeCanPerformActions,
              getToken()
            ).then((permissionsResp) => {
              COSMOS_API.GET(GET_ENDPOINT.GetRolePermission, getToken()).then(
                (rolePermissionsResp) => {
                  var permissions: PermissionsList = {
                    ...permissionsResp.respBody,
                  };
                  var rolePermissions: Record<string, RoleDefinition> = {
                    ...rolePermissionsResp.respBody,
                  };
                  if (emp.isSuperAdmin || emp.isOrganizationAdmin) {
                    var orgListFetch = COSMOS_API.GET(
                      GET_ENDPOINT.GetOrganizationsList,
                      getToken()
                    ).then((orgList) => {
                      if (
                        orgList.ok &&
                        orgList.respBody !== null &&
                        orgList.respBody !== undefined &&
                        orgList.respBody.organizations.length > 0
                      ) {
                        let sortedOrgList = orgList.respBody.organizations.sort(
                          (
                            a: { id: string; name: string },
                            b: { id: string; name: string }
                          ) => a.name >= b.name
                        );
                        return {
                          organizationId: emp.isSuperAdmin ? sortedOrgList[0].id : emp.organizationId,
                          organizationList: [...sortedOrgList],
                          multiOrg: emp.isSuperAdmin || (sortedOrgList.length > 1)
                        };
                      } else {
                        return {
                          organizationId: emp.organizationId,
                          multiOrg: emp.isSuperAdmin
                        };
                      }
                    });
                    var appListFetch =
                      emp.isSuperAdmin ?
                        Promise.resolve({
                          adminApplications: [...adminApps],
                          allApplications: { ...allApps }
                        }) :
                        COSMOS_API.POST(
                          { includeAdmin: true },
                          POST_ENDPOINT.GetApplicationsListForOrgs,
                          getToken()
                        ).then((availableApps) => {
                          if (availableApps.ok &&
                            availableApps.respBody != null &&
                            availableApps.respBody != undefined &&
                            availableApps.respBody.applications?.length > 0) {
                            var orgApps: Record<string, ApplicationReference> = {};
                            availableApps.respBody.applications?.forEach((app: any) => {
                              if (app.isActive === undefined || app.isActive)
                                orgApps = { ...orgApps, [app.id]: { id: app.id, name: app.name, isGlobal: app.isGlobal } };
                            });
                            return {
                              adminApplications: Object.keys(orgApps).map(k => { return { id: k, name: orgApps[k].name, role: emp.applicationSpecificRoles?.[k] ?? '', isGlobal: orgApps[k].isGlobal } }),
                              allApplications: { ...orgApps }
                            }
                          } else {
                            return {
                              adminApplications: [...adminApps],
                              allApplications: { ...allApps }
                            }
                          }
                        }
                        );
                    Promise.all([orgListFetch, appListFetch, groupListFetch]).then(([orgListData, appListData, allGroups]) => {
                      setAdminCredentials({
                        ...adminCredentials,
                        id: emp.id,
                        generalPermissions: permissions,
                        isOrganizationAdmin: emp.isOrganizationAdmin,
                        isSuperAdmin: emp.isSuperAdmin,
                        roleSpecificPermissions: rolePermissions,
                        ...orgListData,
                        ...appListData,
                        roles: emp.roles,
                        allGroups: allGroups
                      });
                      setIsLoading(false);
                    });
                  } else {
                    var orgDataFetch = COSMOS_API.POST(
                      { id: emp.organizationId },
                      POST_ENDPOINT.GetOrganizationData,
                      getToken()
                    ).then((orgData) => { return orgData; });
                    Promise.all([orgDataFetch, groupListFetch]).then(([orgData, allGroups]) => {
                      if (
                        orgData.ok &&
                        orgData.respBody !== null &&
                        orgData.respBody !== undefined
                      ) {
                        COSMOS_API.POST(
                          { includeAdmin: false },
                          POST_ENDPOINT.GetApplicationsListForOrgs,
                          getToken()
                        ).then((orgAppData) => {
                          if (orgAppData.ok &&
                            orgAppData.respBody != null &&
                            orgAppData.respBody != undefined) {
                            let orgApps: Record<string, ApplicationReference> = {};
                            orgAppData.respBody.applications?.forEach((app: any) => {
                              if (app.isActive === undefined || app.isActive)
                                orgApps = { ...orgApps, [app.id]: { id: app.id, name: app.name, role: emp.applicationSpecificRoles?.[app.id] ?? '', isGlobal: app.isGlobal } };
                            });
                        Object.keys(allApps).forEach(appKey => { if (orgData.respBody.appList !== null && (orgData.respBody.appList as Array<string>).includes(appKey)) orgApps[appKey] = allApps[appKey] })
                        setAdminCredentials({
                          ...adminCredentials,
                          id: emp.id,
                          adminApplications: [...adminApps],
                          allApplications: { ...orgApps },
                          generalPermissions: permissions,
                          isOrganizationAdmin: emp.isOrganizationAdmin,
                          isSuperAdmin: emp.isSuperAdmin,
                          organizationId: emp.organizationId,
                          roleSpecificPermissions: rolePermissions,
                          organizationList: [
                            {
                              name: orgData.respBody.name,
                              id: orgData.respBody.id,
                              apps: orgData.respBody.apps ?? [],
                              protocol: orgData.respBody.protocol,
                              isScimOrg: orgData.respBody.isScimOrg === true
                            },
                          ],
                              multiOrg: false,
                              allGroups: allGroups,
                              roles: emp.roles,
                            });
                          } else {
                            setAdminCredentials({
                              ...adminCredentials,
                              id: emp.id,
                              adminApplications: [...adminApps],
                              allApplications: { ...allApps },
                              generalPermissions: permissions,
                              isOrganizationAdmin: emp.isOrganizationAdmin,
                              isSuperAdmin: emp.isSuperAdmin,
                              organizationId: emp.organizationId,
                              roleSpecificPermissions: rolePermissions,
                              multiOrg: false,
                              allGroups: allGroups,
                              roles: emp.roles,
                        });
                          }});
                      } else {
                        setAdminCredentials({
                          ...adminCredentials,
                          id: emp.id,
                          adminApplications: [...adminApps],
                          allApplications: { ...allApps },
                          generalPermissions: permissions,
                          isOrganizationAdmin: emp.isOrganizationAdmin,
                          isSuperAdmin: emp.isSuperAdmin,
                          organizationId: emp.organizationId,
                          roleSpecificPermissions: rolePermissions,
                          multiOrg: false,
                          roles: emp.roles,
                          allGroups: allGroups
                        });
                      }

                      setIsLoading(false);
                    });
                  }
                }
              );
            })
          })
        }
      );
    }
  }, [instance, isAuthenticated, inProgress]);

  useEffect(() => {
    if (
      accountInfo !== undefined &&
      JSON.stringify(adminCredentials.allApplications) !== '{}'
    ) {
      callNotificationsAPI();
      callSupportRequestsAPI();
    }
  }, [accountInfo, adminCredentials]);

  const triggerPopUp = (msg_content: string) => {
    setMsg(msg_content);
    setShowMsg(true);
  };

  const notificationsHandler = () => {
    setNotificationsModal(true);
  };

  const callNotificationsAPI = async () => {
    const body: GetNotifications = {
      employeeId: (accountInfo!.idTokenClaims as BasicIDToken)
        .preferred_username,
    };
    COSMOS_API.POST(
      body,
      POST_ENDPOINT.GetNotificationsForEmployee,
      getToken()
    ).then((notificationsResp) => {
      const notificationList = notificationsResp.respBody.sort(
        (a: NotificationObject, b: NotificationObject) =>
          a.tsCreatedAt > b.tsCreatedAt
            ? -1
            : a.tsCreatedAt < b.tsCreatedAt
            ? 1
            : a.id <= b.id
            ? -1
            : 1
      );
      const notificationPaneList: Array<NotificationObject> =
        notificationList.filter((obj: NotificationObject) => {
          if (!obj.adminAcknowledged) {
            return { ...obj };
          } else {
            return false;
          }
        });

      setNotificationList(notificationList);
      setNotificationPane(notificationPaneList);
    });
  };

  const callSupportRequestsAPI = async () => {
    const body: GetSupportRequests = {
      employeeId: (accountInfo!.idTokenClaims as BasicIDToken)
        .preferred_username,
    };
    COSMOS_API.POST(
      body,
      POST_ENDPOINT.GetOpenSupportRequests,
      getToken()
    ).then((supportRequestsResp) => {
      if (supportRequestsResp.respBody.supportRequests) {
        setSupportRequestsList(
          supportRequestsResp.respBody.supportRequests.sort(
            (a: SupportRequestObject, b: SupportRequestObject) =>
              a.createdAt > b.createdAt
                ? -1
                : a.createdAt < b.createdAt
                ? 1
                : a.id <= b.id
                ? -1
                : 1
          )
        );
      }
    });
  };

  const callAcknowledgeNotificationAPI = async (
    notificationId: string,
    notificationApplication: string
  ): Promise<boolean> => {
    const body: AcknowledgeNotification = { notificationId: notificationId };
    let res = await COSMOS_API.POST(
      body,
      POST_ENDPOINT.AcknowledgeNotification,
      getToken()
    );

    if (res.status == 204 || res.ok){

    await callNotificationsAPI();
    return true;
    }
    return false;
  };

  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={{ scopes: [msalScope], redirectUri: window.location.origin, account: accountInfo, extraQueryParameters: { ...((!!uiLocale) && {ui_locales: uiLocale})} }}>
      <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <SnackbarProvider maxSnack={3}>
          {isLoading ? (
            <Paper className="wallpaper">
              <div className="working">Loading</div>
            </Paper>
          ) : (
            <AdminContext.Provider
              value={{ adminCredentials, setAdminCredentials, getToken, uiLocale, getTokenClaims }}
            >
              <div className="stickyFooterWrapper">
                <Paper
                  sx={{
                    backgroundImage: `url(${backgroundUrl})`,
                    backgroundColor: backgroundColor,
                  }}
                  className="wallpaper"
                >
                  <BrowserRouter>
                    {showMsg ? (
                      <PopUp
                        msg={msg}
                        setShowMsg={setShowMsg}
                        showMsg={showMsg}
                      />
                    ) : null}
                    <NotificationsPane
                      showNotificationPane={notificationsModal}
                      key={notificationPane.length}
                      setShowNotificationPane={setNotificationsModal}
                      notificationList={notificationPane}
                      acknowledgeNotification={callAcknowledgeNotificationAPI}
                    />
                    <Nav
                      notificationsHandler={notificationsHandler}
                      numNotifications={notificationPane.length}
                      setSearchReload={setSearchReload}
                      setRequestsReload={setRequestsReload}
                      setGroupsReload={setGroupsReload}
                      name={
                        accountInfo &&
                        (accountInfo!.idTokenClaims as BasicIDToken)
                          ? (accountInfo!.idTokenClaims as BasicIDToken)
                              .given_name +
                            ' ' +
                            (accountInfo!.idTokenClaims as BasicIDToken)
                              .family_name
                          : (accountInfo!.idTokenClaims as BasicIDToken).email
                      }
                    />
                    {!simplifiedUI || adminCredentials.isSuperAdmin ? (
                      <Routes>
                        {/* Instead of something like key={Date.now()} using using some default state values to keep user on edit user page when desired*/}
                        <Route
                          path="/search-users"
                          element={
                            <SearchUsersPage
                              key={searchReload}
                            />
                          }
                        />
                        {AppEnvSetting.showGroupsPage &&
                          <Route
                            path="/groups"
                            element={
                              <GroupsPage
                                key={groupsReload}
                              />
                            }
                          />
                        }
                        {(!AppEnvSetting.disableNewUserCreation) && (
                        <Route
                          path="/new-user-mgmt"
                          element={<NewUserManagementPage triggerPopUp={triggerPopUp} />}
                        >
                          <Route path={`addUser`} element={<AddUser />} />
                          <Route path={`scim`} element={<SCIMApp />} />
                          <Route
                            path={`massPreload`}
                            element={
                              <MassPreload triggerPopUp={triggerPopUp} />
                            }
                          />
                          <Route
                            path={`resendInvite`}
                            element={
                              <ResendInvite />
                            }
                          />
                          <Route index element={<Navigate to='/new-user-mgmt/addUser' />} />
                        </Route>)}
                        <Route
                          path="/review-requests"
                          element={
                            <PermissionRequestsPage
                              requestsReload={requestsReload}
                            />
                          }
                        />
                        <Route
                          path="/notifications"
                          element={
                            <NotificationsPage
                              allApplications={allApplications}
                              notificationList={notificationList}
                              acknowledgeNotification={
                                callAcknowledgeNotificationAPI
                              }
                              callNotificationsAPI={callNotificationsAPI}
                            />
                          }
                        />
                        <Route
                          path="/support-requests"
                          element={
                            <SupportRequestsPage
                              allApplications={allApplications}
                              supportRequestsList={supportRequestsList}
                              acknowledgeNotification={
                                callAcknowledgeNotificationAPI
                              }
                              callSupportRequestsAPI={callSupportRequestsAPI}
                            />
                          }
                        />
                        <Route path="/admin" element={<AdminPage />} />
                        <Route path="/edit-user" element={<EditComponent />} />
                        <Route index element={<Navigate to='/search-users' />} />
                      </Routes>
                    ) : (
                      <Routes>
                        {/* Instead of something like key={Date.now()} using using some default state values to keep user on edit user page when desired*/}
                        <Route path="/search-users"
                          element={<SearchUsersPage
                            key={searchReload}
                          />}
                        />
                        {(!AppEnvSetting.disableNewUserCreation) && (
                        <Route
                          path="/new-user-mgmt"
                          element={<NewUserManagementPage triggerPopUp={triggerPopUp} />}
                        >
                          <Route path={`addUser`} element={<AddUser />} />
                          <Route path={`scim`} element={<SCIMApp />} />
                          <Route
                            path={`massPreload`}
                            element={
                              <MassPreload triggerPopUp={triggerPopUp} />
                            }
                          />
                          <Route
                            path={`resendInvite`}
                            element={
                              <ResendInvite />
                            }
                          />
                          <Route index element={<Navigate to='/new-user-mgmt/addUser' />} />
                        </Route>)}
                        <Route path="/admin" element={<AdminPage />} />
                        <Route path="/edit-user" element={<EditComponent />} />
                        <Route index element={<Navigate to='/search-users' />} />
                      </Routes>
                    )}
                  </BrowserRouter>
                </Paper>
                <Footer />
              </div>
            </AdminContext.Provider>
          )}
        </SnackbarProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </MsalAuthenticationTemplate>
  );
}

export default App;
